import React, { Key, ReactNode } from 'react'

import { motion, PanInfo } from 'framer-motion'

import { Box, Button, SxProps, Theme } from '@mui/material'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'

import { dragCarouselTransition } from '@/hooks/useDragCarousel'

interface DragCarouselTrackProps<T> {
  items: T[]
  activeIndex: number
  trackViewportRef: (node: HTMLDivElement | null) => void
  trackViewportWidth: number
  onDragEnd: (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void
  onClickPrev: () => void
  onClickNext: () => void
  renderItem: (item: T) => ReactNode
  getItemKey: (item: T) => Key
  arrowOffset?: number
  arrowButtonSx?: SxProps<Theme>
  sx?: SxProps<Theme>
}

const DEFAULT_ARROW_BUTTON_SX: SxProps<Theme> = {
  minWidth: 'auto',
  width: 36,
  height: 36,
  padding: 0,
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  boxShadow: 2,
}

function DragCarouselTrack<T>({
  items,
  activeIndex,
  trackViewportRef,
  trackViewportWidth,
  onDragEnd,
  onClickPrev,
  onClickNext,
  renderItem,
  getItemKey,
  arrowOffset = 4,
  arrowButtonSx = DEFAULT_ARROW_BUTTON_SX,
  sx,
}: DragCarouselTrackProps<T>) {
  return (
    <Box position="relative" sx={sx}>
      <Box ref={trackViewportRef} sx={{ overflow: 'hidden' }}>
        <motion.div
          drag={items.length > 1 ? 'x' : false}
          dragConstraints={{ left: -trackViewportWidth * (items.length - 1), right: 0 }}
          dragElastic={0.1}
          animate={{ x: -activeIndex * trackViewportWidth }}
          transition={dragCarouselTransition}
          onDragEnd={onDragEnd}
          style={{ display: 'flex' }}
        >
          {items.map((item) => (
            <Box key={getItemKey(item)} flexShrink={0} style={{ width: trackViewportWidth }}>
              {renderItem(item)}
            </Box>
          ))}
        </motion.div>
      </Box>

      {items.length > 1 && (
        <>
          <Button
            variant="text"
            color="primary"
            onClick={onClickPrev}
            disabled={activeIndex === 0}
            sx={{
              position: 'absolute',
              left: arrowOffset,
              top: '50%',
              transform: 'translateY(-50%)',
              ...arrowButtonSx,
            }}
          >
            <KeyboardArrowLeft />
          </Button>
          <Button
            variant="text"
            color="primary"
            onClick={onClickNext}
            disabled={activeIndex === items.length - 1}
            sx={{
              position: 'absolute',
              right: arrowOffset,
              top: '50%',
              transform: 'translateY(-50%)',
              ...arrowButtonSx,
            }}
          >
            <KeyboardArrowRight />
          </Button>
        </>
      )}
    </Box>
  )
}

export default DragCarouselTrack
